import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Wallet,
  Download,
  Disc3,
  Rocket,
  TrendingUp,
  Handshake,
  Image,
  Contact,
  ShieldCheck,
  Gauge,
  Inbox,
  Banknote,
  LifeBuoy,
  Sparkles,
  Link as LinkIcon,
  Users,
  UserPlus,
  Palette,
  Megaphone,
  PanelsTopLeft,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { SupportWidget } from "@/components/SupportWidget";
import { NotificationCenter } from "@/components/NotificationCenter";
import { Sun, Moon, Music2, UserCircle2, ChevronsUpDown, Check, Building2, Bot, LayoutGrid, BadgeDollarSign, BadgeCheck, Landmark, Map as MapIcon } from "lucide-react";
import InstallApp from "@/components/InstallApp";
import { getPendingSessions, getWithdrawals, getAdminWithdrawals, getAllTickets, managersMe, roadmapMe } from "@/lib/afmApi";

const NAV = [
  {
    to: "/dashboard", label: "Métricas & Insights", icon: LayoutDashboard, tid: "nav-dashboard",
    children: [
      { to: "/dashboard#resumen", label: "Resumen", dot: "#7C5CFF", tid: "sub-resumen" },
      { to: "/dashboard#plataformas", label: "Plataformas", dot: "#EC4899", tid: "sub-plataformas" },
      { to: "/dashboard#audiencia", label: "Audiencia", dot: "#FF0505", tid: "sub-audiencia" },
    ],
  },
  { to: "/accounting", label: "Contabilidad & Regalías", icon: Wallet, tid: "nav-accounting", badgeKey: "withdrawals" },
  { to: "/manager", label: "Mánager SONA", icon: Bot, tid: "nav-manager-ai" },
  { to: "/predictive", label: "Predictivo", icon: TrendingUp, tid: "nav-predictive" },
  { to: "/marketing", label: "Marketing & Ads", icon: Megaphone, tid: "nav-marketing" },
  { to: "/exports", label: "Exportaciones", icon: Download, tid: "nav-exports" },
  { to: "/distribute", label: "Distribuir Música", icon: Rocket, tid: "nav-distribute", sub: true, needCatalog: true },
  { to: "/catalog", label: "Catálogo", icon: Disc3, tid: "nav-catalog", sub: true },
  { to: "/covers", label: "Carátulas", icon: Image, tid: "nav-covers", sub: true },
  { to: "/split-sheets", label: "Split Sheets", icon: Handshake, tid: "nav-split-sheets", badgeKey: "sessions", sub: true },
  { to: "/hoja-de-ruta", label: "Hoja de ruta", icon: MapIcon, tid: "nav-roadmap", sub: true, roadmap: true },
  { to: "/epk", label: "EPK / Prensa", icon: Contact, tid: "nav-epk" },
  { to: "/perfil-artista", label: "Mi Perfil de Artista", icon: UserCircle2, tid: "nav-artist-profile" },
  { to: "/mi-sello", label: "Mi sello / Artistas", icon: Building2, tid: "nav-manager", manager: true, sub: true, exact: true },
  { to: "/mi-sello/finanzas", label: "Finanzas del sello", icon: BadgeDollarSign, tid: "nav-manager-finance", manager: true, sub: true, finance: true },
  { to: "/admin", label: "Panel Admin", icon: Gauge, tid: "nav-admin-overview", admin: true, exact: true },
  { to: "/admin/accounts", label: "Gestión de Cuentas", icon: UserPlus, tid: "nav-admin-accounts", admin: true },
  { to: "/admin/managers", label: "Managers y sellos", icon: Building2, tid: "nav-admin-managers", admin: true },
  { to: "/admin/hoja-de-ruta", label: "Hoja de ruta (Admin)", icon: MapIcon, tid: "nav-admin-roadmap", admin: true },
  { to: "/admin/panorama", label: "Panorama del catálogo", icon: LayoutGrid, tid: "nav-admin-panorama", admin: true },
  { to: "/admin/firmados", label: "Artistas firmados", icon: BadgeCheck, tid: "nav-admin-signed", admin: true },
  { to: "/admin/finanzas", label: "Finanzas de sellos", icon: Landmark, tid: "nav-admin-finance", admin: true },
  { to: "/admin/access-requests", label: "Solicitudes de Acceso", icon: UserPlus, tid: "nav-admin-access", admin: true },
  { to: "/admin/releases", label: "Bandeja Lanzamientos", icon: Inbox, tid: "nav-admin-releases", admin: true },
  { to: "/admin/withdrawals", label: "Retiros (Admin)", icon: Banknote, tid: "nav-admin-withdrawals", admin: true, badgeKey: "adminWithdrawals" },
  { to: "/admin/support", label: "Soporte (Admin)", icon: LifeBuoy, tid: "nav-admin-support", admin: true, badgeKey: "adminSupport" },
  { to: "/admin/pitching", label: "Pitching IA", icon: Sparkles, tid: "nav-admin-pitching", admin: true },
  { to: "/admin/smartlinks", label: "Smart Links (Admin)", icon: LinkIcon, tid: "nav-admin-smartlinks", admin: true },
  { to: "/admin/staff", label: "Equipo & Acceso", icon: Users, tid: "nav-admin-staff", admin: true },
  { to: "/admin/artists", label: "Directorio de Artistas", icon: Music2, tid: "nav-admin-artists", admin: true },
  { to: "/admin/fresh-finds", label: "Fresh Finds (Admin)", icon: Sparkles, tid: "nav-admin-fresh-finds", admin: true },
  { to: "/admin/cover-requests", label: "Solicitudes de Portada", icon: Palette, tid: "nav-admin-cover-requests", admin: true },
  { to: "/admin/marketing", label: "Campañas Marketing", icon: Megaphone, tid: "nav-admin-marketing", admin: true },
  { to: "/admin/presaves", label: "Pre-saves (Admin)", icon: ShieldCheck, tid: "nav-admin", admin: true },
  { to: "/ops", label: "Panel de Operaciones", icon: PanelsTopLeft, tid: "nav-ops", admin: true, external: true, roles: ["admin", "superadmin"] },
];

const ADMIN_ROLES = ["admin", "distribution_admin", "superadmin", "staff"];

const Badge = ({ value, collapsed, active }) => {
  if (!value) return null;
  if (collapsed) return <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#CA0808] shadow-[0_0_8px_#CA0808]" />;
  return (
    <span className={`ml-auto rounded-full px-2 py-0.5 font-mono text-[10px] font-bold tabular-nums ${active ? "bg-[#CA0808] text-white" : "bg-[#CA0808] text-white"}`}>
      {value}
    </span>
  );
};

const WorkspaceSwitcher = ({ user, isAdmin, collapsed, mgr }) => {
  const spaces = [
    mgr?.manager
      ? { id: "artist", label: mgr.manager.name, sub: mgr.role === "artist" ? "Artista" : "Sello / Manager", initial: (mgr.manager.name || "M").charAt(0).toUpperCase() }
      : { id: "artist", label: user?.artist_stage_name || user?.name || "Mi espacio", sub: "Cuenta de artista", initial: (user?.name || "A").charAt(0).toUpperCase() },
    ...(isAdmin ? [{ id: "afm", label: "Always Fresh Music", sub: "Administración", initial: "AF" }] : []),
  ];
  const [ws, setWs] = useState(() => localStorage.getItem("afm_workspace") || spaces[0].id);
  const [open, setOpen] = useState(false);
  const active = spaces.find((s) => s.id === ws) || spaces[0];
  const pick = (id) => { setWs(id); localStorage.setItem("afm_workspace", id); setOpen(false); };
  if (collapsed) return <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-[#270A0A] text-xs font-bold text-white ring-1 ring-white/10" title={active.label}>{active.initial}</div>;
  return (
    <div className="relative">
      <button type="button" data-testid="workspace-switcher" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 text-left transition-colors hover:bg-white/[0.08]">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#270A0A] text-xs font-bold text-white ring-1 ring-[#CA0808]/40">{active.initial}</span>
        <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-white">{active.label}</span><span className="block truncate text-[10px] uppercase tracking-wider text-[#9CA3AF]">{active.sub}</span></span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
      </button>
      {open && (
        <div data-testid="workspace-menu" className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-xl border border-white/10 bg-[#1b0607] shadow-2xl">
          {spaces.map((s) => (
            <button key={s.id} type="button" data-testid={`workspace-option-${s.id}`} onClick={() => pick(s.id)} className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-white/[0.06] ${s.id === ws ? "text-white" : "text-[#9CA3AF]"}`}>
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#270A0A] text-[11px] font-bold text-white">{s.initial}</span>
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
              {s.id === ws && <Check className="h-4 w-4 text-[#CA0808]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SidebarContent = ({ onNav, collapsed, onToggle }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [counts, setCounts] = useState({ sessions: 0, withdrawals: 0, adminWithdrawals: 0, adminSupport: 0 });
  const [mgr, setMgr] = useState(null); // subcuenta de manager (marca: logo y colores)
  useEffect(() => { if (user?.id) managersMe().then((d) => setMgr(d?.manager ? d : null)).catch(() => {}); }, [user?.id]);
  const [rmOn, setRmOn] = useState(false); // Hoja de ruta: solo si Always la activó para esta cuenta o su sello
  useEffect(() => { if (user?.id) roadmapMe().then((d) => setRmOn(!!d?.enabled)).catch(() => setRmOn(false)); }, [user?.id]);
  const brandLogo = mgr?.manager?.logo_url ? (mgr.manager.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${mgr.manager.logo_url}` : mgr.manager.logo_url) : "";

  useEffect(() => {
    if (!user?.id) return;
    getPendingSessions(user.id).then((d) => setCounts((c) => ({ ...c, sessions: Array.isArray(d) ? d.length : 0 }))).catch(() => {});
    getWithdrawals(user.id).then((d) => setCounts((c) => ({ ...c, withdrawals: Array.isArray(d?.withdrawals) ? d.withdrawals.filter((w) => w.status === "pending" || w.status === "approved").length : 0 }))).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !isAdmin) return;
    const loadAdmin = () => {
      getAdminWithdrawals(user.id, "pending").then((d) => setCounts((c) => ({ ...c, adminWithdrawals: Array.isArray(d?.withdrawals) ? d.withdrawals.length : 0 }))).catch(() => {});
      getAllTickets(user.id, "").then((d) => {
        const list = Array.isArray(d) ? d : (d?.tickets || []);
        const open = list.filter((t) => ["pending", "open"].includes(t.status)).length;
        setCounts((c) => ({ ...c, adminSupport: open }));
      }).catch(() => {});
    };
    loadAdmin();
    const iv = setInterval(loadAdmin, 30000);
    return () => clearInterval(iv);
  }, [user?.id, isAdmin]);

  const handleLogout = async () => { await signOut(); navigate("/login"); };
  const dashActive = location.pathname.startsWith("/dashboard");

  return (
    <div className="flex h-full flex-col overflow-hidden text-white" style={mgr?.manager ? { borderTop: `3px solid ${mgr.manager.color_primary}`, background: mgr.manager.color_accent } : undefined}>
      <div className={`flex items-center py-5 ${collapsed ? "justify-center px-2" : "justify-between px-5"}`}>
        {collapsed ? (
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#CA0808] font-display text-sm font-extrabold text-white">A</div>
        ) : (
          brandLogo ? (
            <div className="flex items-center gap-2"><img src={brandLogo} alt={mgr.manager.name} className="h-10 max-w-[140px] object-contain" /><span className="text-[9px] uppercase tracking-wider text-white/40">by Always Fresh</span></div>
          ) : <LogoMark className="h-10 w-10 text-white" />
        )}
        {onToggle && (
          <button onClick={onToggle} data-testid="sidebar-toggle" className={`hidden rounded-lg p-1.5 text-[#9CA3AF] transition-colors hover:bg-white/10 hover:text-white lg:block ${collapsed ? "absolute right-2 top-6" : ""}`} title={collapsed ? "Expandir" : "Colapsar"}>
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}
      </div>
      <div className={collapsed ? "px-2 pb-3" : "px-3 pb-3"}><WorkspaceSwitcher user={user} isAdmin={isAdmin} collapsed={collapsed} mgr={mgr} /></div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto border-t border-white/[0.06] px-3 py-4">
        {!collapsed && <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6B7280]">Menú</div>}
        {NAV.filter((item) => (!item.admin || isAdmin) && (!item.manager || mgr) && (!item.roles || item.roles.includes(user?.role))
          && (!item.roadmap || isAdmin || rmOn)
          && (!item.finance || (mgr?.manager?.finance_enabled && mgr?.perms?.accounting))
          && (isAdmin || !mgr || (item.sub && (!item.needCatalog || mgr.perms?.catalog)))).map((item, idx, arr) => (
          <div key={item.to}>
            {!collapsed && item.admin && !arr[idx - 1]?.admin && <div className="px-3 pb-2 pt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6B7280]">Administración</div>}
            {item.external ? (
              <a
                href={item.to}
                data-testid={item.tid}
                onClick={onNav}
                title={collapsed ? item.label : undefined}
                className={`group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[#9CA3AF] transition-all duration-200 hover:bg-white/[0.05] hover:text-white ${collapsed ? "justify-center" : ""}`}
              >
                <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.5} />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </a>
            ) : (
            <NavLink
              to={item.to}
              data-testid={item.tid}
              end={item.exact}
              onClick={onNav}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all duration-200 ${collapsed ? "justify-center" : ""} ${
                  isActive
                    ? "bg-[#270A0A] font-semibold text-white shadow-[inset_0_0_0_1px_rgba(202,8,8,0.35)]"
                    : "font-medium text-[#9CA3AF] hover:bg-white/[0.05] hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-[#CA0808] shadow-[0_0_10px_#CA0808]" />}
                  <item.icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? "text-[#FF8A90]" : ""}`} strokeWidth={1.5} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                  {!collapsed && <Badge value={counts[item.badgeKey]} active={isActive} />}
                  {collapsed && <Badge value={counts[item.badgeKey]} collapsed />}
                </>
              )}
            </NavLink>
            )}

            {/* Submenu (chromatic dots) */}
            {item.children && dashActive && (
              <div className={`mt-1 space-y-0.5 ${collapsed ? "flex flex-col items-center" : "ml-4 border-l border-white/10 pl-4"}`}>
                {item.children.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    data-testid={c.tid}
                    onClick={onNav}
                    title={collapsed ? c.label : undefined}
                    className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] font-medium text-[#9CA3AF] transition-colors hover:text-white ${collapsed ? "justify-center" : ""}`}
                  >
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: c.dot }} />
                    {!collapsed && <span className="truncate">{c.label}</span>}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
      <InstallApp collapsed={collapsed} />

      <div className="border-t border-white/[0.06] p-4">
        {!collapsed && (
          <div className="mb-3 flex items-center gap-3 rounded-xl bg-white/[0.04] px-3 py-2.5 ring-1 ring-white/10">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#CA0808] text-xs font-bold text-white">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">{user?.name || "Artista"}</div>
              <div className="truncate text-[11px] text-[#9CA3AF]">{user?.email}</div>
            </div>
          </div>
        )}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          title={collapsed ? "Cerrar sesión" : undefined}
          className={`flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 py-2 text-sm font-medium text-[#9CA3AF] transition-colors hover:border-[#CA0808] hover:bg-[#CA0808] hover:text-white ${collapsed ? "px-0" : "px-3"}`}
        >
          <LogOut className="h-4 w-4" /> {!collapsed && "Cerrar sesión"}
        </button>
        {!collapsed && <div className="mt-3 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6B7280]">Tecnología por SONA</div>}
      </div>
    </div>
  );
};

export const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("afm_sidebar_collapsed") === "1");
  const [dark, setDark] = useState(() => localStorage.getItem("afm_theme") === "dark");
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark"); else root.classList.remove("dark");
    localStorage.setItem("afm_theme", dark ? "dark" : "light");
    // El modo oscuro aplica solo dentro del portal: al salir (landing, login, legal, fresh-finds) se restaura el tema claro.
    return () => root.classList.remove("dark");
  }, [dark]);

  const toggle = () => setCollapsed((c) => { const n = !c; localStorage.setItem("afm_sidebar_collapsed", n ? "1" : "0"); return n; });
  const NAV_FLAT = NAV;
  const current = NAV_FLAT.filter((n) => location.pathname.startsWith(n.to)).sort((a, b) => b.to.length - a.to.length)[0];

  return (
    <div className="aura-bg aura-waves relative min-h-screen overflow-clip p-2 sm:p-4" data-testid="portal-shell">
      <div className="dashboard-container relative mx-auto flex min-h-[calc(100vh-1rem)] w-full max-w-[1800px] sm:min-h-[calc(100vh-2rem)]" data-testid="dashboard-container">
        <aside className={`sidebar-dark hidden shrink-0 transition-[width] duration-300 lg:block ${collapsed ? "w-20" : "w-[268px]"}`}>
          <div className="sticky top-0 h-screen max-h-[calc(100vh-2rem)]">
            <SidebarContent collapsed={collapsed} onToggle={toggle} />
          </div>
        </aside>

        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
            <div className="sidebar-dark absolute inset-y-0 left-0 w-[280px] rounded-r-3xl shadow-2xl">
              <SidebarContent collapsed={false} onNav={() => setMobileOpen(false)} />
            </div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/85 px-4 py-3 backdrop-blur-md lg:px-8 dark:border-white/10 dark:bg-[#0f0f10]/85">
            <div className="flex items-center gap-3">
              <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Always Fre$h Music</div>
                <h1 className="font-display text-lg font-bold tracking-tight text-black dark:text-white">{current?.label || "Panel"}</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setDark((d) => !d)} data-testid="theme-toggle" aria-label="Cambiar tema" className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10">
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <NotificationCenter />
              <div className="hidden items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 sm:flex">Powered by SONA</div>
            </div>
          </header>

          <main className="min-w-0 flex-1 px-4 pt-6 pb-24 lg:px-8 lg:pt-8 lg:pb-28">{children}</main>
          <footer className="border-t border-slate-200 px-4 py-4 lg:px-8 dark:border-white/10" data-testid="portal-footer">
            <div className="flex flex-col gap-2 text-[11px] text-slate-400 sm:flex-row sm:items-center sm:justify-between sm:pr-44">
              <div>© 2026 Always Fresh Music · Caracas, Venezuela</div>
              <nav className="flex flex-wrap gap-x-4 gap-y-1">
                <Link to="/legal/terminos" className="hover:text-black dark:hover:text-white" data-testid="portal-footer-terminos">Términos de Servicio</Link>
                <Link to="/legal/privacidad" className="hover:text-black dark:hover:text-white" data-testid="portal-footer-privacidad">Privacidad</Link>
                <Link to="/legal/distribucion" className="hover:text-black dark:hover:text-white" data-testid="portal-footer-distribucion">Distribución Digital</Link>
                <Link to="/legal/royalties" className="hover:text-black dark:hover:text-white" data-testid="portal-footer-royalties">Políticas de Regalías</Link>
              </nav>
            </div>
          </footer>
        </div>
      </div>
      <SupportWidget />
    </div>
  );
};
